/**
 * Platform — the six capabilities, and the people who open the app.
 *
 * The seed deck describes WeaveIn as six capabilities that hold each funder's
 * interface: the trust layer, eligibility, encounter capture, funder rules and
 * submission, disbursement and reconciliation. This page is where each one is
 * explained in plain words, with who signs off and whether it is live, and
 * where every module the solution pages used to list on their own is tagged to
 * the capability it belongs to. Developers states the same six in API terms.
 *
 * "Who opens it" stays from the Impact4Health version of this page: it is still
 * the clearest answer to what the app is for.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import ClosedLoop from '@/components/marketing/ClosedLoop';
import CapacityStory from '@/components/marketing/CapacityStory';
import GettingStarted from '@/components/marketing/GettingStarted';
import CommonQuestions from '@/components/marketing/CommonQuestions';
import CapabilitySpine from '@/components/marketing/CapabilitySpine';
import OneEncounter from '@/components/marketing/OneEncounter';
import FunderDiscovery from '@/components/marketing/FunderDiscovery';
import InterfaceBuilder from '@/components/marketing/InterfaceBuilder';
import { CAPABILITIES, LANGUAGES, contactHref } from '@/components/marketing/siteFacts';

const WHO_USES_IT = [
  { role: 'Community health worker or promotora', does: 'Meets the member where they are, on a phone, and records the visit there' },
  { role: 'Discharge nurse or case manager', does: 'Finds services with capacity, sends the referral, sees the confirmation' },
  { role: 'Food bank, shelter or service provider', does: 'Receives the request, responds, records what was delivered' },
  { role: 'Billing lead or finance', does: 'Approves what goes to each funder and reviews what came back unmatched' },
  { role: 'Health plan or county coordinator', does: 'Sees which members were reached and what they actually received' },
];

/**
 * Every module the solution pages listed, filed under the capability it
 * serves. The hubs page alone used to list these three times.
 */
const MODULES: Record<string, string[]> = {
  trust: ['Multilingual outreach and campaigns', 'SMS, WhatsApp and voice', 'Consent recorded per exchange', 'Telehealth visits'],
  eligibility: ['Medi-Cal eligibility checks', 'Plan member files read as data', 'ECM populations of focus', 'Service authorizations (EDI 278)'],
  encounter: ['Care plans and SOAP notes', 'Closed-loop referrals', 'Provider directory and matching', 'Discharge planning from ADT feeds', 'NEMT and housing navigation', 'CHW supervision and encounter review'],
  rules: ['Per-plan rule rows with effective dates', 'Community Supports menus', '837P, plan workbooks and return files', 'Grievance and appeal deadlines', 'Credentialing and OIG/SAM screening', 'HEDIS/Stars gap reporting', 'Funding & Growth: opportunity finder and funder outreach'],
  disbursement: ['Milestone payouts', 'Member incentives on Trust cards', 'Hub-to-CBO payment splits'],
  reconciliation: ['835 remittance matching', 'Funds-flow ledger with monthly close', 'Exceptions queue for finance'],
};

/** Deck slide A3, by category rather than by competitor. */
const COMPARISON: { row: string; values: (boolean | string)[] }[] = [
  { row: 'Bought by the CBO as its own system of record', values: [true, 'Payer-side', 'Payer-side', 'Provider-side', false] },
  { row: 'Works across every funder the organization bills', values: [true, false, false, 'Claims only', 'Grants only'] },
  { row: 'Per-plan rules: menu, rates, modifiers, filing window', values: [true, false, 'In-program', 'Standard payers', false] },
  { row: 'Submits the plan’s own way: workbook, portal, 837', values: [true, false, false, '837 only', false] },
  { row: 'Proof a service was not billed to two funders', values: [true, false, false, false, false] },
  { row: 'Cost allocation and grant drawdown', values: ['2027', false, false, false, true] },
];
const COLUMNS = ['WeaveIn', 'Referral networks', 'Payer-side program platforms', 'RCM & clearinghouses', 'Fund accounting'];

const Cell: React.FC<{ v: boolean | string; first: boolean }> = ({ v, first }) => {
  if (v === true) return <Check className={`w-5 h-5 mx-auto ${first ? 'text-v2accent' : 'text-gray-400'}`} aria-label="Yes" />;
  if (v === false) return <Minus className="w-5 h-5 mx-auto text-gray-300" aria-label="No" />;
  return <span className={`text-xs ${first ? 'font-semibold text-v2accent' : 'text-gray-500 dark:text-gray-400'}`}>{v}</span>;
};

export default function PlatformPage() {
  usePageMeta(SEO_ROUTES['/platform']);

  return (
    <div className="bg-white dark:bg-gray-950">
      <MarketingHeader />

      <section className="bg-paper border-b border-v2border text-ink">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-4">The platform</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            An app for the people who do the work.<br />
            <span className="text-v2accent">A ledger for every funder behind it.</span>
          </h1>
          <p className="text-xl text-ink-500 mb-4 max-w-3xl">
            A community health worker records a visit once, on a phone, in the field. Behind that one record, agents handle
            each funder&rsquo;s interface &mdash; eligibility, the plan&rsquo;s own rules, submission the plan&rsquo;s own way,
            payment and the match back to the visit &mdash; and a named person approves every step.
          </p>
          <p className="text-base text-ink-500 max-w-3xl mb-8">
            The field app runs in English and Spanish. Members are reached in {LANGUAGES} languages, by text, WhatsApp, voice or
            in person.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/funder-coverage"
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
            >
              See funder coverage
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/impact"
              className="inline-flex items-center px-8 py-4 border-2 border-v2border text-ink text-lg font-semibold rounded-lg hover:bg-ink hover:text-paper transition-colors"
            >
              See the results
            </Link>
          </div>
        </div>
      </section>

      <CapabilitySpine showStatus showApproves />

      <InterfaceBuilder />

      <OneEncounter />

      <FunderDiscovery />

      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3 text-center">Who opens it</h2>
          <p className="text-base text-gray-600 dark:text-gray-300 text-center mb-10">
            One member&rsquo;s story touches five people. Each sees their own part of it.
          </p>
          <div className="divide-y divide-gray-200 dark:divide-gray-800 border-y border-gray-200 dark:border-gray-800">
            {WHO_USES_IT.map(({ role, does }) => (
              <div key={role} className="py-5 md:flex md:items-baseline md:gap-8">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 md:w-2/5">{role}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 md:w-3/5 mt-1 md:mt-0">{does}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClosedLoop />

      <CapacityStory />

      <section className="py-16 bg-white dark:bg-gray-950" id="modules">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Modules, by capability</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-3xl">
            Everything a hub, plan or provider program runs on, filed under the capability it serves. Turn on what your
            programs need; the record underneath is the same.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c) => (
              <div key={c.key} className="rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3">{c.name}</h3>
                <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300">
                  {(MODULES[c.key] ?? []).map((m) => (
                    <li key={m} className="flex gap-2">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-v2accent shrink-0" aria-hidden="true" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Where each category stops</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-3xl">
            Others bill on the payer&rsquo;s side, inside the one program they administer. WeaveIn is the community
            organization&rsquo;s own system of record, across every funder it bills.
          </p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left p-4 font-semibold text-gray-500 dark:text-gray-400">Capability</th>
                  {COLUMNS.map((c, i) => (
                    <th key={c} className={`p-4 text-center font-semibold ${i === 0 ? 'text-v2accent bg-v2accent-soft' : 'text-gray-500 dark:text-gray-400'}`}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(({ row, values }) => (
                  <tr key={row} className="border-b last:border-0 border-gray-100 dark:border-gray-800">
                    <td className="p-4 text-gray-800 dark:text-gray-100">{row}</td>
                    {values.map((v, i) => (
                      <td key={i} className={`p-4 text-center ${i === 0 ? 'bg-v2accent-soft' : ''}`}>
                        <Cell v={v} first={i === 0} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white dark:bg-gray-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base text-gray-700 dark:text-gray-200">
            The same six capabilities in API terms are on{' '}
            <Link to="/developers" className="font-semibold text-v2accent hover:underline">Developers</Link>; the security
            posture is on{' '}
            <Link to="/security" className="font-semibold text-v2accent hover:underline">Security</Link>. To see it with your
            own programs,{' '}
            <Link to={contactHref('deliver', 'platform walkthrough')} className="font-semibold text-v2accent hover:underline">
              book a walkthrough
            </Link>
            .
          </p>
        </div>
      </section>

      <CommonQuestions />
      <GettingStarted />
      <MarketingFooter />
    </div>
  );
}
